import { useState } from 'react';
import '../App.css';
import { IpaType } from '../../phonemes/ipa';
import { speakPhonemes, stopEspeak } from '../../speak/speakPhonemes';
import { ColumnAlign } from '../widgets/ColumnAlign';
import { SetName, findLexicalSets, lexicalSets, lexicalSetsWikiOrdered } from '../../rules/findLexicalSets';
import { LexicalUnit, LexicalUnits } from './LexicalUnits';
import { englishAccentRules } from '../../ruleSets/accentRules';
import { ApplySets } from './applySets';
import { replaceLexicalTokens } from '../../rules/replaceLexicalSets';
import { PhthongPicker } from './PhthongPicker';
import { SaveLoadBar } from './SaveLoadBar';
import { deleteRule, getSavedRuleList, saveRule } from '../../rules/persistRules';
import { CheckBox } from '../widgets/CheckBox';
import { IpaKeyboard } from '../widgets/IpaKeyboard';

/** UI for changing the phonemes used for English lexical sets */
export function CustomSets() {
  const [applySets] = useState(new ApplySets());
  const [customName, setCustomName] = useState('');
  const [allAccents, setAllAccents] = useState(getAllAccents());
  const [selectedAccent, setSelectedAccent] = useState(Object.getOwnPropertyNames(englishAccentRules)[0]);
  const [nonRhotic, setNonRhotic] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [units, setUnits] = useState(applySets.getUnits());
  const [currentSet, setCurrentSet] = useState(lexicalSets.filter(ls => ls.name === lexicalSetsWikiOrdered[0])[0]);
  const [sampleText, setSampleText] = useState('enter sample text to use for testing a custom accent');
  const [sampleAsSets, setSampleAsSets] = useState('');
  const [sampleAsIpa, setSampleAsIpa] = useState('');

  /** save the current set of rules under the given name */
  function saveAccentRules(accentName: string, isNonRhotic: boolean) {
    if (accentName.length > 0) {
      saveRule(accentName, applySets.getRules(), isNonRhotic);
      setAllAccents(addAccentToList(allAccents, accentName));
    }
  }

  /** user typed in a new name for their custom rule */
  function onNewName(customName: string, forceSave: boolean) {
    setCustomName(customName);
    if (forceSave) {
      saveAccentRules(customName, nonRhotic);
    }
  }

  /** select an accent to use as a seed for lexical sets */
  function onSelectAccent(name: string, isCustom: boolean, rhotic: boolean) {
    applySets.useAccent(name, isCustom);
    setUnits(applySets.getUnits());
    setSelectedAccent(name);
    setNonRhotic(!rhotic);
    // update IPA
    updateSampleResults();
  }

  /** delete saved accent & remove from UI */
  function onDeleteAccent(accent: string) {
    deleteRule(accent);
    setAllAccents(deleteAccentFromList(allAccents, accent));
  }

  /** change whether current accent is rhotic & update sample text */
  function toggleRhotic() {
    applySets.changeRhotic(nonRhotic);
    setNonRhotic(!nonRhotic);
    setUnits(applySets.getUnits());
    saveAccentRules(customName, !nonRhotic);
    // update IPA
    updateSampleResults();
  }

  /** select a lexical set */
  function clickUnit(u: LexicalUnit) {
    // change current lexical set
    const current = lexicalSets.filter(ls => ls.name === u.name)[0];
    setCurrentSet(current);
    // speak lexical set
    const phonemes = applySets.runRules(u.name);
    speakPhonemes(phonemes, undefined, applySets.getAddPhonemeLength());
  }

  /** phoneme associated with current lexical set is being changed */
  function phonemeChanged(phoneme: IpaType) {
    onChangePhoneme(currentSet.name, phoneme);
  }

  /** phoneme associated with named lexical set is being changed */
  function onChangePhoneme(setName: SetName, phoneme: IpaType) {
    // remember new phoneme
    applySets.changePhoneme(setName, phoneme);
    saveAccentRules(customName, nonRhotic);
    setUnits(applySets.getUnits());
    // speak lexical set
    const phonemes = applySets.runRules(setName);
    speakPhonemes(phonemes, undefined, applySets.getAddPhonemeLength());
    // update IPA
    updateSampleResults();
  }

  function onChangeLength(phoneme: IpaType, length: number) {
    applySets.changeLength(phoneme, length);
    saveAccentRules(customName, nonRhotic);
    setUnits(applySets.getUnits());
    updateSampleResults();
  }

  /** when sample text is changed, also update the corresponding lexical set token area */
  function changeSampleText(text: string) {
    setSampleText(text);

    const tokenized = findLexicalSets(text);
    const lookup = lexicalSets.map(set => '(' + set.name + ')');
    const asSets = replaceLexicalTokens(tokenized, lookup);
    setSampleAsSets(asSets);

    setSampleAsIpa(applySets.runRules(text));
  }

  function speakSampleText() {
    const ipa = sampleAsIpa.length !== 0 ? sampleAsIpa : applySets.runRules(sampleText);
    speakPhonemes(ipa, undefined, applySets.getAddPhonemeLength());
  }

  function updateSampleResults() {
    if (sampleText.length > 0 && sampleAsIpa.length === 0) {
      // haven't yet initialized the derived fields
      changeSampleText(sampleText);
    } else {
      // just update IPA
      setSampleAsIpa(applySets.runRules(sampleText));
    }
  }

  const { toEdit, toDisplay, ref } = applySets.getSetPhonemes(currentSet.name, selectedAccent);
  const allPhonemes = showAll ? applySets.getAllPhonemes() : undefined;
  return (<>
    <SaveLoadBar allAccents={allAccents} onNewName={onNewName} onSelectAccent={onSelectAccent} onDeleteAccent={onDeleteAccent} />
    <CheckBox checked={nonRhotic} onChange={toggleRhotic}>non-rhotic</CheckBox>
    <br />

    <ColumnAlign>{[
      <LexicalUnits units={units} nonRhotic={nonRhotic} onClick={clickUnit} onChangePhoneme={onChangePhoneme} onChangeLength={onChangeLength} key={1} />,
      <span key={2}>
        <CheckBox checked={showAll} onChange={s => setShowAll(s)}>show all sets</CheckBox>
        <br />
        <PhthongPicker toEdit={toEdit} toDisplay={toDisplay} reference={ref} list={allPhonemes} changed={phonemeChanged} />
        <br />
      </span>,
      <span key={3}></span>,
    ]}</ColumnAlign>

    <br />
    <table><tbody>
      <tr>
        <td className="clean">sample text:</td>
        <td>
          <textarea cols={80} rows={1} spellCheck={false} value={sampleText}
            onChange={(event) => changeSampleText(event.target.value)} />
        </td>
      </tr>
      <tr>
        <td className="clean">lexical sets:</td>
        <td>
          <textarea cols={80} rows={1} spellCheck={false} value={sampleAsSets} readOnly={true} />
        </td>
      </tr>
      <tr>
        <td className="clean">as IPA:</td>
        <td>
          <IpaKeyboard cols={80} rows={1} value={sampleAsIpa} onChange={setSampleAsIpa} />
        </td>
      </tr>
      <tr></tr>
      <tr>
        <td className="clean"></td>
        <td className="clean">
          <button onClick={() => speakSampleText()}>speak</button>
          &nbsp;&nbsp;&nbsp;
          <button onClick={() => stopEspeak()}>stop</button>
        </td>
      </tr>
    </tbody></table>
  </>);
}

function getAllAccents(): string[] {
  const built = Object.getOwnPropertyNames(englishAccentRules);
  const custom = getSavedRuleList();
  const all = built.concat(custom.map(name => `Custom: ${name}`));
  return all;
}

function addAccentToList(accents: string[], newAccent: string): string[] {
  const name = `Custom: ${newAccent}`;
  return accents.includes(name) ? accents : accents.concat(name);
}

function deleteAccentFromList(accents: string[], remove: string): string[] {
  const name = `Custom: ${remove}`;
  return accents.filter(n => n !== name);
}
